/**
 * Flightline UI <-> Worker contracts and view-models (DTOs).
 *
 * This file is the single source of truth for everything that crosses the
 * Web Worker boundary (API_CONTRACTS.md, extended by Phases 2 and 3). It holds
 * pure types plus two tiny runtime helpers (`CABIN_CLASSES`, `assertNever`),
 * so it is safe to import from both the UI thread and /src/worker.
 *
 * The rich domain entities live in `./index` (the canonical Step 1 models).
 * The types below are *projections* of those entities, flattened and made
 * structured-clone friendly for postMessage. Where a view shares a concept with
 * a domain entity it is deliberately given a distinct name (`AirportView`,
 * `FleetAircraftView`, `RouteAnalyticsView`, `PassengerDemandView`,
 * `LedgerSnapshot`) so the two can never be confused in an import list.
 */

import { SEAT_CLASSES } from './index';
import type {
  AcquisitionTerms,
  AcquisitionType,
  AircraftType,
  CabinConfiguration,
  ClassMap,
  FuelHedgeContract,
  SeatClass,
} from './index';
import type { EventNotification, FuelPriceState, MacroEvent } from '../events/types';
import type { AirportInfrastructure, HubAirportView, InfrastructureProject } from '../infrastructure/types';
import type { CandidateFlightSchedule, CompetitorAirline, PricingUpdate } from '../ai/types';
import type { CheckKind } from '../economics/operatingCost';
import type { FleetAcquisitionState } from '../fleet/types';
import type { DayPlannerSnapshot } from '../routing/dayPlanner';
import type { DemandInertiaSnapshot } from '../demand/demandInertia';
import type { RouteMarketPreview } from '../routing/routePlannerEngine';
import type { MarketReachView, MarketSurveyRow } from '../demand/marketViews';

/** The canonical catalogue entry and cabin layout are shared as-is across the boundary. */
export type { AcquisitionType, AircraftType, CabinConfiguration } from './index';
/** Macro event and fuel market records are plain data from the event module, shared as-is. */
export type { EventNotification, FuelPriceState, MacroEvent, MacroEventState } from '../events/types';

/* ------------------------------------------------------------------ */
/* Cabin classes                                                       */
/* ------------------------------------------------------------------ */

export type CabinClass = SeatClass;

export const CABIN_CLASSES: readonly CabinClass[] = SEAT_CLASSES;

export type PerCabin<T> = ClassMap<T>;

/* ------------------------------------------------------------------ */
/* Time                                                                */
/* ------------------------------------------------------------------ */

/** Simulated wall clock as the schedule UI sees it. `minuteOfDay` may be fractional. */
export interface SimClock {
  readonly dayIndex: number;
  readonly minuteOfDay: number;
}

/* ------------------------------------------------------------------ */
/* World                                                               */
/* ------------------------------------------------------------------ */

/** Airport projection sent to the UI (map, schedule validation, pickers). */
export interface AirportView {
  readonly id: string; // IATA, "JFK"
  readonly name: string;
  readonly city: string;
  readonly country: string;
  readonly lat: number;
  readonly lng: number;
  readonly runwayLengthMeters: number;
  /** 24 entries, max aircraft movements per UTC hour. */
  readonly slotCapacityHourly: readonly number[];
  readonly hasMaintenanceBase: boolean;
}

/* ------------------------------------------------------------------ */
/* Fleet                                                               */
/* ------------------------------------------------------------------ */

export type MaintenanceCheckType = 'A_CHECK' | 'C_CHECK' | 'D_CHECK';

export type AircraftStatus = 'IN_FLIGHT' | 'GATE_TURNAROUND' | 'MAINTENANCE' | 'IDLE';

/** Read-only view of one fleet aircraft, streamed to the UI. */
export interface FleetAircraftView {
  readonly registration: string; // "FL-001"
  readonly typeId: string;
  readonly purchaseType: AcquisitionType;
  /** Where the aircraft sits when it has no scheduled legs. */
  readonly baseAirportId: string;
  readonly cabin: CabinConfiguration;
  readonly flightHoursSinceACheck: number;
  readonly flightHoursSinceCCheck: number;
  readonly status: AircraftStatus;
  readonly locationAirportId: string;
  readonly currentLegId: string | null;
  readonly passengersOnboard: number;
  readonly seats: number;
  /** Load factor of the flight in progress, or of the most recent flight when on the ground. */
  readonly loadFactorPct: number;
  /** Absolute sim minute of the next scheduled departure, or null if the aircraft has no schedule. */
  readonly nextDepartureAbsMinute: number | null;
  /** Absolute sim minute at which the current status ends (arrival / turnaround / maintenance). */
  readonly statusUntilAbsMinute: number | null;
  readonly maintenanceKind: MaintenanceCheckType | null;
  readonly hoursToACheck: number;
}

/* ------------------------------------------------------------------ */
/* Schedule                                                            */
/* ------------------------------------------------------------------ */

/**
 * A daily-recurring flight leg. All times are simulation time (UTC).
 * `departureMinute` is minute-of-day [0, 1440). A leg whose
 * departureMinute + blockMinutes passes midnight wraps into the next day.
 */
export interface FlightLeg {
  readonly id: string;
  readonly aircraftReg: string;
  readonly origin: string; // AirportView.id
  readonly destination: string; // AirportView.id
  readonly departureMinute: number;
  readonly blockMinutes: number;
}

/** A published leg as streamed to the UI: the schedule slot plus its fares and geometry. */
export interface LegView extends FlightLeg {
  readonly fares: PerCabin<number>;
  readonly distanceKm: number;
  readonly seatsPerDeparture: number;
}

/** A one-off maintenance visit on an absolute simulation-minute window. */
export interface MaintenanceEvent {
  readonly id: string;
  readonly aircraftReg: string;
  readonly check: MaintenanceCheckType;
  readonly airportId: string;
  /** Absolute sim minutes: dayIndex * 1440 + minuteOfDay. */
  readonly startAbsMinute: number;
  readonly durationMinutes: number;
}

export interface FlightSchedule {
  readonly legs: readonly FlightLeg[];
  readonly maintenance: readonly MaintenanceEvent[];
}

/**
 * Payload of CREATE_FLIGHT_LEG. `fares` is optional: omitted cabins are priced
 * from the market reference fare for the stage length.
 */
export interface NewFlightLeg {
  readonly aircraftReg: string;
  readonly origin: string;
  readonly destination: string;
  readonly departureMinute: number;
  readonly blockMinutes: number;
  readonly fares?: Partial<PerCabin<number>>;
}

export type NewMaintenanceEvent = Omit<MaintenanceEvent, 'id'>;

/* ------------------------------------------------------------------ */
/* Live operations                                                     */
/* ------------------------------------------------------------------ */

export interface FlightStatusSnapshot {
  readonly legId: string;
  readonly aircraftReg: string;
  readonly origin: string;
  readonly destination: string;
  readonly progressPct: number; // 0 to 100% between airports
  readonly passengersOnboard: number;
}

/* ------------------------------------------------------------------ */
/* Finance                                                             */
/* ------------------------------------------------------------------ */

/**
 * Cash book snapshot. "Today" values cover the current (or, inside a
 * TICK_DAILY event, the just-closed) simulation day.
 */
export interface LedgerSnapshot {
  readonly cashUSD: number;
  readonly revenueTodayUSD: number;
  readonly operatingCostTodayUSD: number;
  readonly fuelCostTodayUSD: number;
  readonly fuelBurnKgToday: number;
  /** Live jet-fuel spot price in US dollars per gallon, after macro shocks. */
  readonly fuelSpotPerGallonUSD: number;
  /** Combined multiplier from active fuel shock events (1 = none). */
  readonly fuelShockMultiplier: number;
  /** Today's saving from fuel hedges against spot (memo; 0 while no hedges are held). */
  readonly fuelHedgeSavingsTodayUSD: number;
  readonly revenueTotalUSD: number;
  readonly operatingCostTotalUSD: number;
}

/* ------------------------------------------------------------------ */
/* Route analytics                                                     */
/* ------------------------------------------------------------------ */

/**
 * O&D demand outcome for one route over the most recent daily tick.
 *
 * Invariant maintained by the worker (the UI tolerates small rounding drift):
 *   totalPotential = carried + unmet
 *   unmet = capacitySpill + priceLeakage + scheduleLeakage + comfortLeakage
 */
export interface PassengerDemandView {
  /** Total O&D potential demand (gravity model x seasonality x day-of-week x hour). */
  readonly totalPotential: number;
  /** Passengers actually carried by the player's airline in this O&D market. */
  readonly carried: number;
  /** Passengers that wanted to travel but were not carried. */
  readonly unmet: number;
  /** Lost because every flight on the route was full. */
  readonly capacitySpill: number;
  /** Lost to the outside option because of fares. */
  readonly priceLeakage: number;
  /** Lost to poor departure times or too few frequencies. */
  readonly scheduleLeakage: number;
  /** Lost to the outside option because of cabin comfort. */
  readonly comfortLeakage: number;
}

/** One simulated day of route history, used for the trend chart. */
export interface RouteDailySnapshot {
  readonly day: number;
  readonly loadFactorPct: number;
  readonly netProfitUSD: number;
}

/** One origin-destination passenger flow carried on a route (possibly connecting). */
export interface ODFlow {
  readonly origin: string;
  readonly destination: string;
  readonly passengers: number;
  /** True when the passenger connects over this leg rather than flying it end to end. */
  readonly connecting: boolean;
}

export interface RouteAnalyticsView {
  readonly legId: string;
  readonly origin: string;
  readonly destination: string;
  readonly aircraftReg: string;

  // --- Financial ledger (most recent daily tick) ---
  readonly revenueUSD: number;
  readonly operatingCostUSD: number;
  /** Available seat miles flown on the route in the same period. */
  readonly availableSeatMiles: number;
  readonly loadFactorPct: number;
  readonly scheduledFlights: number;
  readonly operatedFlights: number;
  readonly cancelledFlights: number;
  readonly passengers: number;
  readonly seats: number;
  readonly fuelBurnKg: number;
  readonly topFlows: readonly ODFlow[];

  // --- Pricing ---
  readonly currentFares: PerCabin<number>;
  /** Market reference fare per cabin, USD. */
  readonly marketBaselineFares: PerCabin<number>;
  /** Own-price elasticity of demand per cabin (negative numbers). */
  readonly priceElasticityByCabin: PerCabin<number>;
  /** Seats installed per cabin on this route. 0 means the cabin is not sold. */
  readonly seatsByCabin: PerCabin<number>;

  // --- Demand diagnostics ---
  readonly demand: PassengerDemandView;

  // --- Live fuel and competitors ---
  /** Fuel bill for this route's flights on the most recent daily tick, USD. */
  readonly fuelCostUSD: number;
  /** The landing-and-take-off share of `fuelCostUSD`: paid per departure, not per hour. */
  readonly ltoFuelCostUSD: number;
  /** Landing fees on MTOW, passenger and terminal charges and noise surcharges, USD. */
  readonly airportFeesUSD: number;
  /** Crew pay and maintenance reserve, USD. Zero for a wet-leased airframe. */
  readonly crewMaintenanceUSD: number;
  /** This route's share of the aircraft's lease or ownership cost, USD. Included in `operatingCostUSD`. */
  readonly capitalChargeUSD: number;
  /** Revenue from passengers on multi-leg itineraries, USD. Included in `revenueUSD`. */
  readonly connectingRevenueUSD: number;
  /**
   * Signed feeder proration, USD, included in `revenueUSD`. Positive: the route is a feeder and was credited a
   * share of its passengers' onward fares. Negative: it is the onward leg and gave a share up.
   */
  readonly feederCreditUSD: number;
  /** Local hour (0-24, decimal) of the route's first departure, at the origin. */
  readonly departureLocalHour: number;
  /** Business share of the market's demand, 0-1. */
  readonly businessShare: number;
  /** AI carriers that published fares on this O&D market. */
  readonly competitorCount: number;
  /** Average estimated load factor (%) of those carriers, or null when none is measured. */
  readonly competitorLoadFactorPct: number | null;
  /** Player load factor minus competitor average, in percentage points; null without competitors. */
  readonly loadFactorGapPts: number | null;

  /** Daily history, oldest first. The UI renders the trailing 30 days. */
  readonly history: readonly RouteDailySnapshot[];
}

/* ------------------------------------------------------------------ */
/* Fleet acquisition view (domain/fleet). Ids are what commands send back. */
/* ------------------------------------------------------------------ */

export interface SecondaryListingView {
  readonly listingId: string;
  readonly typeId: string;
  readonly registration: string;
  readonly manufactureYear: number;
  readonly ageYears: number;
  readonly airframeHours: number;
  readonly conditionPct: number;
  readonly yearsUntilDCheck: number;
  readonly fuelBurnMultiplier: number;
  readonly askingPriceUSD: number;
  /** Sim day on which the listing lapses. */
  readonly expiresOnDay: number;
}

export interface FactoryOrderView {
  readonly id: string;
  readonly typeId: string;
  readonly registration: string;
  /** Absolute sim minute of delivery. */
  readonly deliveryAt: number;
  readonly deliveryLeadMonths: number;
  readonly lockedPriceUSD: number;
  readonly depositUSD: number;
  readonly status: 'OPEN' | 'DELIVERED' | 'CANCELLED';
}

export interface LeaseView {
  readonly registration: string;
  readonly typeId: string;
  readonly subType: LeaseSubTypeCommand;
  readonly origin: 'NEW_LEASE' | 'SALE_LEASEBACK';
  readonly startedAt: number;
  readonly endsAt: number;
  readonly termMonths: number;
  readonly paymentsMade: number;
  readonly monthlyPaymentUSD: number;
  readonly depositUSD: number;
  readonly status: 'ACTIVE' | 'ENDED' | 'TERMINATED' | 'OWNED';
  /** Finance leases only; null for dry and wet leases. */
  readonly outstandingPrincipalUSD: number | null;
}

export interface FleetAcquisitionView {
  /** Lessor market in force; the UI uses it to preview prices. */
  readonly market: { readonly annualInterestRatePct: number; readonly demandIndex: number };
  readonly catalogueMonth: number;
  readonly listings: SecondaryListingView[];
  readonly factoryOrders: FactoryOrderView[];
  readonly leases: LeaseView[];
}

/* ------------------------------------------------------------------ */
/* UI -> Worker commands (API_CONTRACTS.md section 1, plus Steps 8-10) */
/* ------------------------------------------------------------------ */

/**
 * UPDATE_TICKET_PRICE. API_CONTRACTS.md defines `fareEconomy` and
 * `fareBusiness`; premium economy and first are optional, backwards-compatible
 * additions. An omitted fare means "leave unchanged".
 */
export interface UpdateTicketPriceCommand {
  readonly type: 'UPDATE_TICKET_PRICE';
  readonly legId: string;
  readonly fareEconomy: number;
  readonly fareBusiness: number;
  readonly farePremiumEconomy?: number;
  readonly fareFirst?: number;
}

/**
 * How an aircraft is financed. The contract's legacy `'LEASE' | 'BUY'` pair is
 * still accepted (LEASE = operating lease, BUY = purchase).
 */
export type AcquisitionRequest = AcquisitionType | 'LEASE' | 'BUY';

/**
 * How PURCHASE_AIRCRAFT sources the airframe: a new build from the OEM with a
 * lead time of 6–24 months, or a used airframe from the monthly secondary listings.
 */
export type PurchaseChannelCommand =
  | { readonly kind: 'FACTORY_ORDER'; readonly deliveryLeadMonths: number }
  | { readonly kind: 'SECONDARY_LISTING'; readonly listingId: string };

/** LEASE_AIRCRAFT sub-type: dry (airline crews and maintains), wet/ACMI (lessor does), or finance. */
export type LeaseSubTypeCommand = 'DRY' | 'WET' | 'FINANCE';

export type UICommand =
  | { readonly type: 'START_SIMULATION'; readonly speedMultiplier: number }
  | { readonly type: 'PAUSE_SIMULATION' }
  | { readonly type: 'SET_SPEED'; readonly speedMultiplier: number }
  | { readonly type: 'CREATE_FLIGHT_LEG'; readonly leg: NewFlightLeg }
  | UpdateTicketPriceCommand
  | {
      readonly type: 'ACQUIRE_AIRCRAFT';
      readonly typeId: string;
      readonly purchaseType: AcquisitionRequest;
      readonly cabin: CabinConfiguration;
    }
  | { readonly type: 'REFIT_AIRCRAFT'; readonly registration: string; readonly cabin: CabinConfiguration }
  | { readonly type: 'DELETE_FLIGHT_LEG'; readonly legId: string }
  | { readonly type: 'SCHEDULE_MAINTENANCE'; readonly event: NewMaintenanceEvent }
  | { readonly type: 'UPGRADE_AIRPORT_INFRASTRUCTURE'; readonly airportId: string; readonly project: InfrastructureProject }
  | {
      readonly type: 'PURCHASE_AIRCRAFT';
      readonly typeId: string;
      readonly channel: PurchaseChannelCommand;
      readonly cabin: CabinConfiguration;
    }
  | {
      readonly type: 'LEASE_AIRCRAFT';
      readonly typeId: string;
      readonly leaseSubType: LeaseSubTypeCommand;
      readonly termMonths: number;
      /** Airframe age in years when the lease starts (0 for new). */
      readonly ageYears: number;
      readonly cabin: CabinConfiguration;
    }
  | { readonly type: 'SELL_LEASEBACK_AIRCRAFT'; readonly registration: string; readonly termMonths: number }
  | { readonly type: 'TERMINATE_LEASE'; readonly registration: string }
  /** Asks the worker for a snapshot; it answers with SAVE_GAME_EXPORT. Taken at the current hour, whatever the clock is doing. */
  | { readonly type: 'SAVE_GAME_REQUEST' }
  /** Asks for a market preview of an unflown pair. Answered by ROUTE_OPPORTUNITY_PREVIEW. */
  | { readonly type: 'PREVIEW_ROUTE_OPPORTUNITY'; readonly originId: string; readonly destinationId: string }
  /** Asks for the catchment pools around an airport. Answered by MARKET_REACH. */
  | { readonly type: 'REQUEST_MARKET_REACH'; readonly airportId: string }
  /** Asks for the surveyed markets, optionally limited to routes touching an airport. Answered by MARKET_SURVEY. */
  | { readonly type: 'REQUEST_MARKET_SURVEY'; readonly originId: string | null }
  /** Asks for one origin's demand row to every airport in the database. Answered by ORIGIN_DEMAND_ROWS. */
  | { readonly type: 'REQUEST_ORIGIN_DEMAND'; readonly originId: string }
  /** Replaces the whole world with a save. Pauses the clock; answers with WORKER_READY (or SIMULATION_ERROR). */
  | { readonly type: 'HYDRATE_GAME_STATE'; readonly save: SaveGameState }
  /** Starts a fresh company at the chosen base, with no aircraft. Pauses the clock. Answers with WORKER_READY. */
  | { readonly type: 'NEW_GAME'; readonly hqAirportId: string };

/* ------------------------------------------------------------------ */
/* Worker -> UI events (API_CONTRACTS.md section 2, plus state sync)   */
/*                                                                    */
/* `hour` is the absolute simulation hour (hours since SIM_EPOCH).    */
/* `day` is the number of fully completed simulation days.            */
/* ------------------------------------------------------------------ */

/** Seat share of one airline on one O&D market, from the published schedules. */
export interface AirlineSeatShareView {
  /** Airline code; the player's airline is `PLAYER`. */
  readonly airlineId: string;
  /** Share of daily seats on the market, 0–100. */
  readonly seatSharePct: number;
}

export interface RouteSeatShareView {
  readonly routeKey: string; // "LHR-CDG"
  readonly originId: string;
  readonly destinationId: string;
  readonly shares: AirlineSeatShareView[];
}

/**
 * One destination in an origin's demand row. Passengers are per average day,
 * from the gravity model: the total is gravity x distance factor, then capped
 * by the saturation ceiling, so it can sit below the product of the two.
 */
export interface OriginDemandRowView {
  readonly destinationId: string;
  readonly destinationName: string;
  readonly distanceKm: number;
  readonly totalPassengersPerDay: number;
  readonly businessPassengersPerDay: number;
  readonly leisurePassengersPerDay: number;
  readonly businessSharePct: number;
  /** Gravity term K·(Pop·GDP)A·(Pop·GDP)B / distance^1.5, before distance factor and saturation. */
  readonly rawGravityPassengersPerDay: number;
  /** 0–1 factor applied to the gravity term for distance (ground substitution, long-haul decay). */
  readonly distanceAttenuation: number;
}

export type CoreWorkerEvent =
  | { readonly type: 'TICK_HOURLY'; readonly hour: number; readonly activeFlights: FlightStatusSnapshot[] }
  | {
      /**
       * End-of-day state snapshot. Carries the live fuel price, the active macro
       * events and the AI carriers' seat shares alongside the route analytics.
       */
      readonly type: 'TICK_DAILY';
      readonly day: number;
      readonly analytics: RouteAnalyticsView[];
      readonly ledger: LedgerSnapshot;
      readonly macroEvents: MacroEvent[];
      readonly fuel: FuelPriceState;
      readonly competitorShares: RouteSeatShareView[];
    }
  | { readonly type: 'SIMULATION_ERROR'; readonly error: string };

/* ------------------------------------------------------------------ */
/* Macro events and AI competitors (Steps 11 and 12)                   */
/* ------------------------------------------------------------------ */

export type CompetitorActionKind = 'ROUTE_LAUNCH' | 'FARE_CHANGE';

/** One AI carrier decision, logged for the news feed. */
export interface CompetitorActionView {
  readonly kind: CompetitorActionKind;
  readonly competitorId: string;
  readonly competitorName: string;
  readonly routeKey: string;
  readonly summary: string;
  /** Annual slot licence cost paid for a launch, USD; 0 for a fare change. */
  readonly annualSlotCostUSD: number;
}

/** One hour of an airport's slot auction, as the market view shows it. */
export interface SlotAuctionHourView {
  readonly hourUtc: number;
  readonly capacity: number;
  readonly committed: number;
  readonly requested: number;
  readonly awarded: number;
  /** Reserve (floor) price per slot, annual USD. */
  readonly reservePriceUSD: number;
  /** Uniform price every winner pays for a slot this hour, annual USD. */
  readonly clearingPriceUSD: number;
}

/** Result of one airport's slot auction in the latest competitor cycle. */
export interface SlotAuctionAirportView {
  readonly airportId: string;
  readonly slotsAwarded: number;
  readonly slotsRemaining: number;
  readonly deniedRequests: number;
  /** Licence income to the airport: clearing price times slots granted, USD. */
  readonly totalLicenseRevenueUSD: number;
  readonly hours: readonly SlotAuctionHourView[];
}

/**
 * Incremental world events, sent as they happen during the daily close. The
 * `day` field is the sim day the change takes effect on.
 */
export type WorldEvent =
  | { readonly type: 'MACRO_EVENT_ANNOUNCED'; readonly day: number; readonly event: MacroEvent; readonly notification: EventNotification }
  | { readonly type: 'MACRO_EVENT_UPDATED'; readonly day: number; readonly event: MacroEvent; readonly notification: EventNotification }
  | {
      readonly type: 'FUEL_PRICE_CHANGED';
      readonly day: number;
      readonly previousSpotPerGallonUSD: number;
      readonly fuel: FuelPriceState;
    }
  | { readonly type: 'COMPETITOR_ACTION_LOGGED'; readonly day: number; readonly action: CompetitorActionView }
  | { readonly type: 'SLOT_AUCTIONS_CLEARED'; readonly day: number; readonly auctions: readonly SlotAuctionAirportView[] };

/**
 * Additive state-sync events: static world data, fleet and schedule state, and
 * a live ledger so the header cash balance reacts to commands immediately.
 */
export type StateSyncEvent =
  | {
      readonly type: 'WORKER_READY';
      readonly hour: number;
      readonly hqAirportId: string;
      readonly airports: AirportView[];
      readonly aircraftTypes: AircraftType[];
    }
  | {
      readonly type: 'NETWORK_STATE';
      readonly hour: number;
      readonly legs: LegView[];
      readonly maintenance: MaintenanceEvent[];
      readonly fleet: FleetAircraftView[];
      readonly acquisitions: FleetAcquisitionView;
      readonly infrastructure: AirportInfrastructure[];
      /** One card per airport the airline serves or has built at, with project quotes. */
      readonly hubs: HubAirportView[];
      readonly ledger: LedgerSnapshot;
    }
  | { readonly type: 'RUN_STATE'; readonly running: boolean; readonly speedMultiplier: number }
  /** Answer to SAVE_GAME_REQUEST. Requests are answered in the order they arrive. */
  | { readonly type: 'SAVE_GAME_EXPORT'; readonly save: SaveGameState }
  /** Answer to PREVIEW_ROUTE_OPPORTUNITY. Computed from the live network; not stored in state. */
  | { readonly type: 'ROUTE_OPPORTUNITY_PREVIEW'; readonly preview: RouteMarketPreview }
  /** Answer to REQUEST_MARKET_REACH. */
  | { readonly type: 'MARKET_REACH'; readonly reach: MarketReachView }
  /** Answer to REQUEST_MARKET_SURVEY, computed from the live published schedules. */
  | { readonly type: 'MARKET_SURVEY'; readonly originId: string | null; readonly rows: MarketSurveyRow[] }
  /**
   * Answer to REQUEST_ORIGIN_DEMAND: every other airport in the database, strongest
   * first. Empty `rows` when the origin is not in the database. `day` is the number
   * of completed days when the row was computed.
   */
  | {
      readonly type: 'ORIGIN_DEMAND_ROWS';
      readonly day: number;
      readonly originId: string;
      readonly gravityConstant: number;
      readonly rows: readonly OriginDemandRowView[];
    }
  /** Sent once at start-up: the world data, before any company exists. The UI offers a base choice from `airports`. */
  | { readonly type: 'WORLD_LOADED'; readonly airports: AirportView[]; readonly aircraftTypes: AircraftType[] };

export type WorkerEvent = CoreWorkerEvent | StateSyncEvent | WorldEvent;

/** The ORIGIN_DEMAND_ROWS event, named for the UI state that holds the latest answer. */
export type OriginDemandAnswer = Extract<WorkerEvent, { readonly type: 'ORIGIN_DEMAND_ROWS' }>;

/* ------------------------------------------------------------------ */
/* Save games                                                          */
/* ------------------------------------------------------------------ */

/** Identifies a Flightline save file. */
export const SAVE_GAME_FORMAT = 'flightline-save' as const;
/** Bumped whenever SaveGameState changes shape. Files from other versions are rejected, not migrated. */
export const SAVE_GAME_VERSION = 2 as const;

/** A Map flattened to its entries, so the state survives JSON. */
export type SavedEntries<V> = ReadonlyArray<readonly [string, V]>;

export interface SavedLeg {
  readonly id: string;
  readonly flightNumber: string;
  readonly aircraftReg: string;
  readonly origin: string;
  readonly destination: string;
  readonly departureMinute: number;
  readonly blockMinutes: number;
  readonly distanceKm: number;
  readonly fares: PerCabin<number>;
}

/** A flight in the air (or on the ground, pre-arrival) with its booked load. */
export interface SavedActiveFlight {
  readonly legId: string;
  readonly origin: string;
  readonly destination: string;
  readonly departAbs: number;
  readonly arriveAbs: number;
  readonly blockMinutes: number;
  readonly passengers: PerCabin<number>;
  readonly totalPassengers: number;
  readonly seats: number;
  readonly revenueUSD: number;
  /** Feeder proration and connecting revenue inside `revenueUSD`; absent in saves made before they were tracked. */
  readonly feederCreditUSD?: number;
  readonly connectingRevenueUSD?: number;
  readonly flows: SavedEntries<number>;
}

/** An airframe, without its derived cabin metrics (those are recomputed from the type and cabin). */
export interface SavedFleetAircraft {
  readonly reg: string;
  readonly typeId: string;
  readonly acquisition: AcquisitionType;
  readonly terms: AcquisitionTerms;
  readonly deliveredAbs: number;
  readonly manufactureYear: number;
  readonly wetLease: boolean;
  readonly cabin: CabinConfiguration;
  readonly baseAirportId: string;
  readonly locationAirportId: string;
  readonly active: SavedActiveFlight | null;
  readonly readyAtAbs: number;
  readonly maintenanceUntilAbs: number;
  readonly maintenanceKind: CheckKind | null;
  readonly hoursSinceA: number;
  readonly hoursSinceC: number;
  readonly lastDCheckAbs: number;
  readonly lastLoadFactorPct: number;
}

/** A leg's counters for the day in progress. */
export interface SavedLegDayStats {
  readonly legId: string;
  readonly scheduled: number;
  readonly operated: number;
  readonly cancelled: number;
  readonly seats: number;
  readonly passengers: number;
  readonly revenueUSD: number;
  readonly costUSD: number;
  readonly fuelCostUSD: number;
  readonly fuelBurnKg: number;
  /** Cost split and feeder counters; absent in saves made before they were tracked. */
  readonly airportFeesUSD?: number;
  readonly ltoFuelCostUSD?: number;
  readonly crewMaintenanceUSD?: number;
  readonly feederCreditUSD?: number;
  readonly connectingRevenueUSD?: number;
  readonly seatsByCabin: PerCabin<number>;
  readonly passengersByCabin: PerCabin<number>;
  readonly flows: SavedEntries<number>;
}

export interface SavedLegHistory {
  readonly legId: string;
  readonly records: readonly RouteDailySnapshot[];
}

export interface SaveGameCounters {
  readonly legCounter: number;
  readonly regCounter: number;
  readonly maintenanceCounter: number;
}

export interface SaveWorldSettings {
  readonly worldSeed: number;
  readonly hqAirportId: string;
  readonly reputation: number;
}

/**
 * The complete mutable state of one game. Restoring it and running on gives
 * the same events as the run it was taken from. Static world data (airports,
 * aircraft types) is not included: it ships with the build.
 */
export interface SaveGameState {
  readonly format: typeof SAVE_GAME_FORMAT;
  readonly version: typeof SAVE_GAME_VERSION;
  /** Absolute sim hour at which the snapshot was taken. */
  readonly savedAtHour: number;
  readonly planDirty: boolean;
  readonly world: SaveWorldSettings;
  readonly counters: SaveGameCounters;
  /** Company finance: cash, the day's and lifetime totals, and the jet-fuel market in force. */
  readonly ledger: LedgerSnapshot;
  readonly fuelPrice: FuelPriceState;
  readonly fuelHedges: readonly FuelHedgeContract[];
  readonly infrastructure: readonly AirportInfrastructure[];
  /** Lessor market indicators, secondary listings, factory orders, active leases and airframes. */
  readonly fleetAcquisition: FleetAcquisitionState;
  readonly fleet: readonly SavedFleetAircraft[];
  /** Published schedule: the player's daily legs and their fares. */
  readonly legs: readonly SavedLeg[];
  readonly maintenance: readonly MaintenanceEvent[];
  readonly startedMaintenanceIds: readonly string[];
  readonly dayStats: readonly SavedLegDayStats[];
  readonly history: readonly SavedLegHistory[];
  /** Bookings, planned hours and fractional-demand carry. */
  readonly planner: DayPlannerSnapshot;
  /**
   * Network scalar, recent load factors and O&D demand levels. Optional so that
   * saves written before demand inertia existed still load; they restore to the initial state.
   */
  readonly demand?: DemandInertiaSnapshot;
  readonly macroEvents: readonly MacroEvent[];
  readonly competitors: readonly CompetitorAirline[];
  readonly competitorRotations: readonly CandidateFlightSchedule[];
  readonly pricingUpdates: readonly PricingUpdate[];
}

/** Exhaustiveness helper for discriminated-union switches. */
export function assertNever(value: never): never {
  throw new Error(`Unhandled discriminated union member: ${JSON.stringify(value)}`);
}
